import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInterceptor';
import type { RootState } from '../store';

interface Message {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_type: 'admin' | 'student' | 'coach';
  content: string;
  created_at: string;
  is_read: boolean;
  temp_id?: string;
  pending?: boolean;
}

interface Conversation {
  id: string;
  user_id: string;
  user_name: string;
  user_type: 'student' | 'coach';
  user_avatar?: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
  is_active: boolean;
}

interface MessageState {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  messages: Message[];
  messagesByConversation: Record<string, Message[]>;
  loading: boolean;
  error: string | null;
}

const initialState: MessageState = {
  conversations: [],
  selectedConversation: null,
  messages: [],
  messagesByConversation: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async (filter: 'all' | 'students' | 'coaches') => {
    const response = await axiosInstance.get(`/api/admin/conversations`, {
      params: { type: filter === 'all' ? undefined : filter },
    });
    return response.data;
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async (conversationId: string) => {
    const response = await axiosInstance.get(`/api/admin/conversations/${conversationId}/messages`);
    return { conversationId, messages: response.data };
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async ({ conversation_id, content, temp_id }: { conversation_id: string; content: string; temp_id: string }) => {
    const response = await axiosInstance.post(`/api/admin/conversations/${conversation_id}/messages`, {
      content,
    });
    return { message: response.data, tempId: temp_id };
  }
);

export const markAsRead = createAsyncThunk(
  'messages/markAsRead',
  async (conversationId: string) => {
    await axiosInstance.put(`/api/admin/conversations/${conversationId}/read`);
    return conversationId;
  }
);

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    clearSelectedConversation: (state) => {
      state.selectedConversation = null;
      state.messages = [];
    },
    selectConversation: (state, action: PayloadAction<string>) => {
      const conversation = state.conversations.find((c) => c.id === action.payload) || null;
      state.selectedConversation = conversation;
      state.messages = state.messagesByConversation[action.payload] || [];
      if (conversation) {
        conversation.unread_count = 0;
      }
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
      if (state.selectedConversation) {
        const convId = state.selectedConversation.id;
        if (!state.messagesByConversation[convId]) {
          state.messagesByConversation[convId] = [];
        }
        state.messagesByConversation[convId].push(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch conversations';
      })
      // Fetch messages
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload.messages;
        state.messagesByConversation[action.payload.conversationId] = action.payload.messages;
        const conversation = state.conversations.find(
          (c) => c.id === action.payload.conversationId
        );
        if (conversation) {
          state.selectedConversation = conversation;
          conversation.unread_count = 0;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch messages';
      })
      // Send message
      .addCase(sendMessage.pending, (state, action) => {
        const { conversation_id, content, temp_id } = action.meta.arg;
        const optimistic: Message = {
          id: temp_id,
          temp_id,
          sender_id: 'admin',
          sender_name: 'Admin',
          sender_type: 'admin',
          content,
          created_at: new Date().toISOString(),
          is_read: true,
          pending: true,
        };

        if (state.selectedConversation?.id === conversation_id) {
          state.messages.push(optimistic);
        }

        if (!state.messagesByConversation[conversation_id]) {
          state.messagesByConversation[conversation_id] = [];
        }
        state.messagesByConversation[conversation_id].push(optimistic);

        const conversation = state.conversations.find((c) => c.id === conversation_id);
        if (conversation) {
          conversation.last_message = content;
          conversation.last_message_time = optimistic.created_at;
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { message, tempId } = action.payload;
        const replaceOptimistic = (list: Message[]) =>
          list.map((m) => (m.id === tempId || m.temp_id === tempId ? message : m));

        state.messages = replaceOptimistic(state.messages);

        const convId = state.selectedConversation?.id;
        if (convId && state.messagesByConversation[convId]) {
          state.messagesByConversation[convId] = replaceOptimistic(state.messagesByConversation[convId]);
        }

        if (state.selectedConversation && !state.messages.find((m) => m.id === message.id)) {
          state.messages.push(message);
        }

        if (state.selectedConversation) {
          const conversation = state.conversations.find(
            (c) => c.id === state.selectedConversation!.id
          );
          if (conversation) {
            conversation.last_message = message.content;
            conversation.last_message_time = message.created_at;
          }
        }
        // Update last message in conversation
      })
      .addCase(sendMessage.rejected, (state, action) => {
        const { temp_id, conversation_id } = action.meta.arg;
        state.messages = state.messages.filter((m) => m.id !== temp_id && m.temp_id !== temp_id);
        if (state.messagesByConversation[conversation_id]) {
          state.messagesByConversation[conversation_id] = state.messagesByConversation[conversation_id].filter(
            (m) => m.id !== temp_id && m.temp_id !== temp_id
          );
        }
      })
      // Mark as read
      .addCase(markAsRead.fulfilled, (state, action) => {
        const conversation = state.conversations.find((c) => c.id === action.payload);
        if (conversation) {
          conversation.unread_count = 0;
        }
      });
  },
});

export const { clearSelectedConversation, selectConversation, addMessage } = messageSlice.actions;

// Selectors
export const selectConversations = (state: RootState) => state.messages.conversations;
export const selectSelectedConversation = (state: RootState) => state.messages.selectedConversation;
export const selectMessages = (state: RootState) => state.messages.messages;
export const selectMessagesByConversation = (state: RootState) => state.messages.messagesByConversation;
export const selectMessagesLoading = (state: RootState) => state.messages.loading;
export const selectMessagesError = (state: RootState) => state.messages.error;

export default messageSlice.reducer;
